import React, { useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import LeaveMultiDatePicker from "./LeaveMultiDatePicker";
import { EXCUSE_REASON_CATEGORIES } from "./leaveConstants";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	DatePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Space,
	Modal,
	Table,
	Tag,
	Layout,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Option } = Select;
const { TextArea } = Input;
const { Content } = Layout;

const getJobLabel = (job, gender) => {
	if (job === "목사") return "목사";
	if (job === "전도사") return gender === "남" ? "남전도사" : "여전도사";
	if (job === "교직원") return gender === "남" ? "남직원" : "여직원";
	return job || "-";
};

const ApplyForExcuse = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [userProfile, setUserProfile] = useState(null);
	const [additionalUsersModalVisible, setAdditionalUsersModalVisible] =
		useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState(
		[],
	);
	const [searchText, setSearchText] = useState("");
	const [filteredUsers, setFilteredUsers] = useState([]);

	useEffect(() => {
		message.config({ top: 150 });
		return () => message.config({ top: 8 });
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const profile = await getCurrentUserProfile();
				setUserProfile(profile);
				form.setFieldsValue({
					name: profile.name,
					position:
						profile.classification_name === "교역자"
							? "교역자"
							: "교직원",
					application_date: dayjs(),
					leave_type: "사유서",
				});
			} catch (e) {
				message.error("사용자 정보를 불러오는데 실패했습니다.");
			}
		})();
	}, [form]);

	const fetchAvailableUsers = async () => {
		try {
			const response = await useAxios.get("/users/clergy-staff/");
			if (response.status === 200) {
				setAvailableUsers(response.data);
				setFilteredUsers(response.data);
			}
		} catch (e) {
			message.error("교역자·교직원 목록을 불러오지 못했습니다.");
		}
	};

	const handleOpenAdditionalUsersModal = () => {
		setAdditionalUsersModalVisible(true);
		fetchAvailableUsers();
	};

	useEffect(() => {
		const q = searchText.trim().toLowerCase();
		if (!q) {
			setFilteredUsers(availableUsers);
			return;
		}
		setFilteredUsers(
			availableUsers.filter((u) =>
				String(u.name || "")
					.toLowerCase()
					.includes(q),
			),
		);
	}, [searchText, availableUsers]);

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const dates = (values.leave_dates || []).slice();
			if (!dates.length) {
				message.error("날짜를 하나 이상 선택해주세요.");
				return;
			}
			const formData = {
				leave_type: "사유서",
				reason: values.reason,
				reason_category: values.reason_category,
				additional_users: values.additional_users || [],
				leave_dates: dates
					.sort((a, b) => a.valueOf() - b.valueOf())
					.map((d) => d.format("YYYY-MM-DD")),
			};
			const response = await useAxios.post(
				"/utils/leave/apply/",
				formData,
			);
			if (response.status === 201) {
				message.success("사유서가 접수되었습니다.");
				form.resetFields();
				setSelectedAdditionalUsers([]);
				if (userProfile) {
					form.setFieldsValue({
						name: userProfile.name,
						position:
							userProfile.classification_name ===
							"교역자"
								? "교역자"
								: "교직원",
						application_date: dayjs(),
						leave_type: "사유서",
					});
				}
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"신청 처리 중 오류가 발생했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const disabledDate = (current) => current && current.day() === 0;

	const rowSelection = {
		selectedRowKeys: selectedAdditionalUsers.map((u) => u.id),
		preserveSelectedRowKeys: true,
		onChange: (selectedRowKeys) => {
			setSelectedAdditionalUsers((prev) => {
				const byId = new Map(
					availableUsers.map((u) => [u.id, u]),
				);
				return selectedRowKeys
					.map(
						(id) =>
							byId.get(id) ??
							prev.find((u) => u.id === id),
					)
					.filter(Boolean);
			});
		},
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="사유서" />
			<Layout className="member-layout">
				<LeaveNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="member-content__inner">
							<div className="member-content__form-container page">
								<PageHeader title="사유서" />
								<Card className="surface-card">
									<Form
										form={form}
										layout="vertical"
										onFinish={onFinish}>
										<Form.Item
											name="name"
											label="이름">
											<Input disabled />
										</Form.Item>
										<Form.Item
											name="position"
											label="직분">
											<Select disabled>
												<Option value="교역자">
													교역자
												</Option>
												<Option value="교직원">
													교직원
												</Option>
											</Select>
										</Form.Item>
										<Form.Item
											name="application_date"
											label="신청일">
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												disabled
												format="YYYY-MM-DD"
											/>
										</Form.Item>
										<Form.Item
											name="leave_dates"
											label="대상 날짜"
											rules={[
												{
													required: true,
													message:
														"날짜를 하나 이상 선택해주세요.",
												},
											]}>
											<LeaveMultiDatePicker
												disabledDate={
													disabledDate
												}
											/>
										</Form.Item>
										<Form.Item
											name="reason_category"
											label="세부분류"
											rules={[
												{
													required: true,
													message:
														"세부분류를 선택해주세요",
												},
											]}>
											<Select placeholder="세부분류를 선택하세요">
												{EXCUSE_REASON_CATEGORIES.map(
													(cat) => (
														<Option
															key={
																cat
															}
															value={
																cat
															}>
															{
																cat
															}
														</Option>
													),
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="reason"
											label="사유"
											rules={[
												{
													required: true,
													message:
														"사유를 입력해주세요",
												},
											]}>
											<TextArea rows={4} />
										</Form.Item>
										<Form.Item
											name="additional_users"
											label="추가 해당인">
											<Space
												direction="vertical"
												style={{
													width: "100%",
												}}>
												<Button
													type="dashed"
													onClick={
														handleOpenAdditionalUsersModal
													}
													block
													icon={
														<UserOutlined />
													}>
													추가 해당인
													선택
												</Button>
												{selectedAdditionalUsers.map(
													(user) => (
														<Tag
															key={
																user.id
															}
															closable
															onClose={() => {
																const next =
																	selectedAdditionalUsers.filter(
																		(
																			u,
																		) =>
																			u.id !==
																			user.id,
																	);
																setSelectedAdditionalUsers(
																	next,
																);
																form.setFieldsValue(
																	{
																		additional_users:
																			next.map(
																				(
																					u,
																				) =>
																					u.id,
																			),
																	},
																);
															}}>
															{
																user.name
															}{" "}
															(
															{getJobLabel(
																user.job,
																user.gender,
															)}
															)
														</Tag>
													),
												)}
											</Space>
										</Form.Item>
										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												loading={
													loading
												}
												block>
												신청하기
											</Button>
										</Form.Item>
									</Form>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
			<Modal
				title="추가 해당인 선택"
				open={additionalUsersModalVisible}
				onOk={() => {
					form.setFieldsValue({
						additional_users:
							selectedAdditionalUsers.map(
								(u) => u.id,
							),
					});
					setAdditionalUsersModalVisible(false);
				}}
				onCancel={() =>
					setAdditionalUsersModalVisible(false)
				}
				width={640}>
				<Input.Search
					placeholder="이름 검색"
					value={searchText}
					onChange={(e) =>
						setSearchText(e.target.value)
					}
					style={{ marginBottom: 12 }}
				/>
				<Table
					rowKey="id"
					size="small"
					rowSelection={rowSelection}
					dataSource={filteredUsers}
					pagination={{ pageSize: 8 }}
					columns={[
						{
							title: "이름",
							dataIndex: "name",
						},
						{
							title: "직분",
							dataIndex: "classification_name",
						},
					]}
				/>
			</Modal>
		</ConfigProvider>
	);
};

export default ApplyForExcuse;
